import { forwardRef, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from "react";
import styles from "./styles.module.scss";
import { RiSearchLine } from "react-icons/ri";
import classnames from "classnames";
import { uniqueId } from "lodash";
import { BetterPopover } from "../popover";
import { scroll_style } from "../scrollContainer";
import { MultiSelect } from "./Select";
import { SelectOption } from "./Select";
import { Input, Button, Stack, DebouncedInput } from "@altimateai/lego";

type StyleSize = "sm" | "md" | "lg";

const styleMap: Record<StyleSize, string> = {
  sm: styles.padding_sm,
  md: styles.padding_md,
  lg: styles.padding_lg,
};

interface SearchInputProps {
  defaultValue?: string;
  placeholder?: string;
  size?: StyleSize;
  onChange: (value: string) => void;
  hideOutline?: boolean;
}

const SearchInput = ({
  defaultValue = "",
  placeholder = "Search",
  size = "md",
  onChange,
  hideOutline = false,
}: SearchInputProps) => {
  const [value, setValue] = useState(defaultValue);

  return (
    <div
      className={classnames(styles.debounce_input, styleMap[size], {
        [styles.outline]: !hideOutline,
      })}
    >
      <RiSearchLine />
      <input
        placeholder={placeholder}
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => {
          if (e.key == "Enter") {
            e.stopPropagation();
            onChange(value);
          }
        }}
      />
    </div>
  );
};

interface SimpleInputProps {
  defaultValue?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  size?: StyleSize;
  hideOutline?: boolean;
}

const SimpleInput = ({
  defaultValue = "",
  onChange,
  placeholder,
  size = "md",
  hideOutline = false,
}: SimpleInputProps) => {
  const [value, setValue] = useState(defaultValue);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onChange(value);
    }
  };

  return (
    <div
      className={classnames(styles.simple_input, styleMap[size], {
        [styles.outline]: !hideOutline,
      })}
    >
      <RiSearchLine className={styles.search_icon} onClick={() => onChange(value)} />
      <input
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
};

interface ArrayInputProps {
  defaultValue?: string[];
  placeholder?: string;
  options: SelectOption[];
  onChange?: (values: string[]) => void;
}

export interface ArrayInputHandle {
  getValue: () => SelectOption[];
}

const ArrayInput = forwardRef<ArrayInputHandle, ArrayInputProps>(function ArrayInput(props, ref) {
  const { defaultValue = [], placeholder, options, onChange } = props;
  const trggerRef = useRef<HTMLDivElement>(null);
  const id = useMemo(() => uniqueId("array-input"), []);
  const [value, setValue] = useState<Record<string, boolean>>(() => {
    const _value: Record<string, boolean> = {};
    defaultValue.forEach(item => (_value[item] = true));
    return _value;
  });
  const [dropdownValue, setDropdownValue] = useState<Record<string, boolean>>(value);
  const [inputValue, setInputValue] = useState("");
  useImperativeHandle(
    ref,
    () => ({
      getValue: () => options.filter(item => value[item.value]),
    }),
    [options, value]
  );
  const [triggerWidth, setTriggerWidth] = useState(400);
  useLayoutEffect(() => {
    if (!trggerRef.current) return;
    const { width } = trggerRef.current.getBoundingClientRect();
    setTriggerWidth(width);
  }, []);
  return (
    <Stack direction="column" className="gap-sm">
      {options
        .filter(item => value[item.value])
        .map(item => (
          <div key={item.value} className={classnames(styles.form_input, styles.form_value)}>
            {item.label}
          </div>
        ))}
      <BetterPopover
        id={`popover-${id}`}
        trigger={
          <div ref={trggerRef} className={classnames(styles.form_input)}>
            <input
              className={styles.search_input}
              placeholder={placeholder}
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
            />
            <div className={styles.add_btn}>Add</div>
          </div>
        }
        render={({ close }) => (
          <Stack
            direction="column"
            className="p-2 al-h-full"
            style={{
              width: `${triggerWidth}px`,
              overflowY: "auto",
            }}
          >
            <div style={{ maxHeight: "400px" }} className={scroll_style}>
              <MultiSelect
                options={options.filter(
                  item =>
                    typeof item.label === "string" &&
                    item.label.toLowerCase().includes(inputValue.toLowerCase())
                )}
                onChange={key => setDropdownValue(prev => ({ ...prev, [key]: !prev[key] }))}
                value={key => dropdownValue[key] || false}
              />
            </div>
            <Stack className="gap-sm">
              <Button
                size="sm"
                variant="default"
                onClick={() => {
                  close();
                  const newValue = { ...value, ...dropdownValue };
                  setValue(newValue);
                  onChange?.(options.filter(item => newValue[item.value]).map(item => item.value));
                }}
              >
                Apply
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  close();
                }}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        )}
      />
    </Stack>
  );
});

export const ExpandableFormInput = (props: React.ComponentProps<typeof Input>) => (
  <Input className={styles.form_input} {...props} />
);

export { DebouncedInput, SimpleInput, ArrayInput, SearchInput };
